import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

import { index } from '@last/core'

import { getFloorplans } from '@/api/floorplan'
import { useTabsStore } from '@/store/tabs'
import type { Floorplan, TableWithTabs } from '@/types'

export const useFloorplanStore = defineStore(
  'tables',
  () => {
    const floorplans = shallowRef<Record<string, Floorplan>>({})

    const tables = computed(() => {
      return index(
        Object.values(floorplans.value).flatMap(f => f.tables),
        'id'
      )
    })

    const floorplanIds = computed(() => Object.keys(floorplans.value))

    const floorplanList = computed(() => {
      return Object.values(floorplans.value)
        .sort((a, b) => a.position - b.position)
        .map(floorplan => {
          return {
            ...floorplan,
            tables: floorplan.tables.map(table => {
              const tabsStore = useTabsStore()
              const tabs = tabsStore.getByTableId(table.id)
              return {
                ...table,
                tabIds: tabs.map(tab => tab.id)
              }
            })
          }
        })
    })

    const allTables = computed<TableWithTabs[]>(() => {
      return floorplanList.value.flatMap(floorplan => floorplan.tables)
    })

    function getTableFloorplan(tableId: string) {
      const table = tables.value[tableId]
      return floorplans.value[table?.floorplanId]
    }

    async function refreshFloorplans() {
      floorplans.value = await getFloorplans()
    }

    return {
      floorplanIds,
      floorplans,
      floorplanList,
      tables,
      allTables,
      getTableFloorplan,
      refreshFloorplans
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFloorplanStore, import.meta.hot))
}
